import { injectable } from "inversify";
import { makeAutoObservable, runInAction } from "mobx";
import { formatMmSs } from "../utils/format-time";
import type { IDemoClockManager } from "@/lib/types/demo-session";

const FINAL_MINUTE_MS = 60 * 1000;
const PAUSE_LATE_MS = 30 * 1000;
const TICK_MS = 300;

/** Separate from RemainingTime: the deadline is server-owned and can move once (auto-extend). */
@injectable()
export class DemoClockStore implements IDemoClockManager {
  deadlineAt: string | null = null;
  ending: boolean = false;
  wasExtended: boolean = false;
  paused: boolean = false;
  pausedAt: string | null = null;
  autoResumeAt: string | null = null;
  lastResumeReason: "manual" | "auto" | null = null;
  private now: number = Date.now();
  private interval: ReturnType<typeof setInterval> | null = null;
  private frozenRemainingMs: number | null = null;

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });
  }

  get remainingMs(): number {
    if (this.paused) return this.frozenRemainingMs ?? 0;
    if (!this.deadlineAt) return 0;
    return Math.max(0, new Date(this.deadlineAt).getTime() - this.now);
  }

  get displayTime(): string {
    return formatMmSs(this.remainingMs);
  }

  get isBoundary(): boolean {
    return this.wasExtended && this.remainingMs <= FINAL_MINUTE_MS;
  }

  get pauseRemainingMs(): number {
    if (!this.autoResumeAt) return 0;
    return Math.max(0, new Date(this.autoResumeAt).getTime() - this.now);
  }

  get pauseDisplayTime(): string {
    return formatMmSs(this.pauseRemainingMs);
  }

  get isPauseLate(): boolean {
    return this.paused && this.pauseRemainingMs <= PAUSE_LATE_MS;
  }

  get pauseFraction(): number {
    if (!this.pausedAt || !this.autoResumeAt) return 0;
    const total = new Date(this.autoResumeAt).getTime() - new Date(this.pausedAt).getTime();
    if (total <= 0) return 0;
    return Math.min(1, Math.max(0, this.pauseRemainingMs / total));
  }

  setDeadline(deadlineAt: string, extended: boolean = false): void {
    if (!Number.isFinite(Date.parse(deadlineAt))) return;

    runInAction(() => {
      this.deadlineAt = deadlineAt;
      if (extended) this.wasExtended = true;
    });
    this.ensureTicking();
  }

  setLocalFallback(durationMs: number): void {
    if (this.deadlineAt) return;
    this.setDeadline(new Date(Date.now() + durationMs).toISOString());
  }

  setEnding(): void {
    runInAction(() => {
      this.ending = true;
    });
  }

  pause(pausedAt: string, autoResumeAt: string | null): void {
    if (autoResumeAt !== null && !Number.isFinite(Date.parse(autoResumeAt))) return;
    // Read before `paused` flips — the getter returns the frozen value after.
    const current = this.remainingMs;
    runInAction(() => {
      if (!this.paused) this.frozenRemainingMs = current;
      this.paused = true;
      this.pausedAt = pausedAt;
      this.autoResumeAt = autoResumeAt;
      // Reset per pause so a freeze end can't inherit a rep pause's "auto" toast.
      this.lastResumeReason = null;
    });
    this.ensureTicking();
  }

  resume(reason: "manual" | "auto"): void {
    if (!this.paused) return;
    runInAction(() => {
      this.paused = false;
      this.pausedAt = null;
      this.autoResumeAt = null;
      this.frozenRemainingMs = null;
      this.lastResumeReason = reason;
    });
  }

  resumeFromFreeze(): void {
    if (!this.paused) return;
    runInAction(() => {
      this.paused = false;
      this.pausedAt = null;
      this.autoResumeAt = null;
      this.frozenRemainingMs = null;
    });
  }

  clearStaleEnding(): void {
    if (!this.ending || !this.deadlineAt) return;
    if (new Date(this.deadlineAt).getTime() - Date.now() > FINAL_MINUTE_MS) {
      runInAction(() => {
        this.ending = false;
      });
    }
  }

  reset(): void {
    this.stopTicking();
    runInAction(() => {
      this.deadlineAt = null;
      this.ending = false;
      this.wasExtended = false;
      this.paused = false;
      this.pausedAt = null;
      this.autoResumeAt = null;
      this.frozenRemainingMs = null;
      this.lastResumeReason = null;
    });
  }

  private ensureTicking(): void {
    if (this.interval) return;
    this.interval = setInterval(() => {
      runInAction(() => {
        this.now = Date.now();
      });
    }, TICK_MS);
  }

  private stopTicking(): void {
    if (this.interval) {
      clearInterval(this.interval);
      this.interval = null;
    }
  }
}
